import { createRouter, createWebHistory, type RouteLocationNormalized } from "vue-router";
import { storeToRefs } from "pinia";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { useAccountStore } from "@/stores/accountStore";
import { DashboardPermission } from "@/helpers/apiDashboardWrappers";
import { showNotification } from "@/helpers/notificationHelper";

import HttpErrorView from "@/views/HttpErrorView.vue";

const ENTERPRISE_SUBSCRIPTION_LEVEL_ID = 3;

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "Home",
      redirect: () => ({
        name: "SubscrsLicenses"
      })
    },
    {
      path: "/subscriptions_licenses",
      name: "SubscrsLicenses",
      component: () => import("@/views/SubscriptionsLicensesView.vue"),
      props: (r) => ({
        openFilter: r.query["openFilter"] === "true" ? true : undefined
      }),
      meta: {
        title: "Subscriptions and Licenses",
        requiredPermissions: [DashboardPermission.LicenseListView]
      },
      children: [
        {
          name: "SubscriptionLevels",
          path: "choose_subscription_level",
          components: { SubscriptionLevelsDialog: () => import("@/components/dialogs/ManageSubscriptionDialog.vue") },
          meta: {
            showSubscriptionLevels: true
          },
          beforeEnter: async (_, __, next) => {
            const { initSubscriptionInfo } = useSubscriptionStore();
            const { subscriptionLevelId, upgradeUrl } = storeToRefs(useSubscriptionStore());

            if (subscriptionLevelId.value == null) {
              await initSubscriptionInfo();
            }

            if (upgradeUrl.value != null || subscriptionLevelId.value === ENTERPRISE_SUBSCRIPTION_LEVEL_ID)
              next({ name: "SubscrsLicenses" });
            else
              next();
          }
        }
      ]
    },
    {
      path: "/license/:licenseId/:arg1?/:arg2?",
      name: "License",
      component: () => import("@/views/LicenseView.vue"),
      props: (r) => ({
        licenseId: r.params["licenseId"],
        dialogType: r.params["arg1"],
        dialogArg: r.params["arg2"]
      }),
      meta: {
        title: "License",
        requiredPermissions: [DashboardPermission.LicenseView],
        setRouteAsActive: "SubscrsLicenses"
      },
    },
    {
      path: "/altium365",
      name: "Altium365",
      component: () => import("@/views/Altium365.vue"),
      meta: {
        title: "Altium 365"
      }
    },
    {
      path: "/overview",
      name: "Overview",
      component: () => import("@/views/OverviewPageView.vue"),
      meta: {
        title: "Overview"
      }
    },
    {
      path: "/payment_invoices/:arg1?",
      name: "PaymentInvoices",
      component: () => import("@/views/PaymentInvoicesView.vue"),
      props: (r) => ({
        dialogType: r.params["arg1"]
      }),
      meta: {
        title: "Payment and Invoices",
        requiredPermissions: [DashboardPermission.BillingInfoView]
      },
    },
    {
      path: "/company_profile",
      name: "CompanyProfile",
      component: () => import("@/views/CompanyProfile.vue"),
      meta: {
        title: "Company Profile",
        requiredPermissions: [DashboardPermission.CompanyProfileView]
      }
    },
    {
      path: "/reports",
      name: "Reports",
      redirect: () => ({
        name: "ReportRealTimeUsage"
      })
    },
    {
      path: "/reports/real-time-usage",
      name: "ReportRealTimeUsage",
      component: () => import("@/views/reports/RealTimeUsageReport.vue"),
      props: (r) => ({
        expandFirstSeatLimitReached: r.query["expandFirstSeatLimitReached"] === "true"
      }),
      meta: {
        title: "Real Time Usage Report",
        requiredPermissions: [DashboardPermission.ReportRealTimeUsage],
        setRouteAsActive: "Reports"
      }
    },
    {
      path: "/reports/real-time-capacity",
      name: "ReportRealTimeCapacity",
      component: () => import("@/views/reports/RealTimeCapacityReport.vue"),
      meta: {
        title: "Real Time Capacity",
        requiredPermissions: [DashboardPermission.ReportRealTimeCapacity],
        setRouteAsActive: "Reports"
      },
      beforeEnter: async (_, __, next) => {
        const accountStore = useAccountStore();
        const currentAccount = await accountStore.getCurrentAccount();

        if (currentAccount?.isAltium !== true)
          next('http404');
        else
          next();
      }
    },
    {
      path: "/reports/usage-log",
      name: "ReportUsageLog",
      component: () => import("@/views/reports/UsageLogReport.vue"),
      meta: {
        title: "Usage Log Report",
        requiredPermissions: [DashboardPermission.ReportUsageLog],
        setRouteAsActive: "Reports"
      }
    },
    {
      path: "/reports/usage-history",
      name: "ReportUsageHistory",
      component: () => import("@/views/reports/UsageHistoryReport.vue"),
      meta: {
        title: "Usage History Report",
        requiredPermissions: [DashboardPermission.ReportUsageHistory],
        setRouteAsActive: "Reports"
      }
    },
    {
      path: "/access_denied",
      name: "AccessDenied",
      component: () => import("@/views/AccessDenied.vue"),
      props: {
        type: "no-permission"
      },
      meta: {
        title: "Access denied",
        noLeftMenu: true
      }
    },
    {
      path: "/unavailable",
      name: "Unavailable",
      component: () => import("@/views/AccessDenied.vue"),
      props: {
        type: "unavailable"
      },
      meta: {
        title: "This page is unavailable",
        noLeftMenu: true
      }
    },
    {
      path: "/:pathMatch(.*)*",
      name: "http404",
      component: HttpErrorView,
      props: () => ({
        errorCode: 404,
      }),
    },
  ],
});

router.beforeEach(async (to) => {
  setPageTitle(to);
});

router.onError((error) => {
  if (error.message.includes("Failed to fetch dynamically imported module")
    || error.message.includes("Importing a module script failed")
    || error.message.includes("Loading chunk")) {
    console.error(error.message);
    showNotification("The application is out of date. The page is being reloaded...", "info");
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  }
})

function setPageTitle(to: RouteLocationNormalized) {
  // This goes through the matched routes from last to first, finding the closest route with a title.
  // e.g., if we have `/some/deep/nested/route` and `/some`, `/deep`, and `/nested` have titles,
  // `/nested`'s will be chosen.
  const nearestWithMetaTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title);

  // If a route with a title was found, set the document (page) title to that value.
  if (nearestWithMetaTitle) {
    document.title = nearestWithMetaTitle.meta.title as string;
  }
  else {
    document.title = "Dashboard";
  }
}

export default router;
